"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ThermometerSnowflake, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
interface TrailerItem {
  id: string;
  title: string;
  tag: string;
  description: string;
  feature?: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
  linkTo?: string;
  linkLabel?: string;
}
const trailers: TrailerItem[] = [{
  id: "standard-refrigerated",
  title: "6 × 8 Refrigerated & Freezer Trailer",
  tag: "Verified specifications",
  description: "All trailers operate using one standard 115-volt, 15-amp electrical circuit and maintain temperatures between −10°F and 40°F when properly connected and operated. Temperature performance may vary based on ambient conditions, loading, door usage, electrical supply, and operating conditions. Optional Honda 3000 generators and interior shelving are available; pricing and availability are confirmed with each quote.",
  feature: "Temperature performance may vary based on ambient conditions, loading, door usage, electrical supply, and operating conditions.",
  image: "https://fridgefleet.com/wp-content/uploads/2026/09/6x8-FF-trailer.png?v=8ebfwpslvg7",
  srcset: "https://fridgefleet.com/wp-content/uploads/2026/09/6x8-FF-trailer.png?v=zmehs7jigbp-wvc-srcset",
  sizes: "https://fridgefleet.com/wp-content/uploads/2026/09/6x8-FF-trailer.png?v=qy0cuqsc70l-wvc-sizes",
  alt: "Fridge Fleet refrigerated trailer parked at a restaurant receiving dock."
}, {
  id: "heavy-duty-freezer",
  title: "7 × 12 Refrigerated & Freezer Trailer",
  tag: "Verified specifications",
  description: "All trailers operate using one standard 115-volt, 15-amp electrical circuit and maintain temperatures between −10°F and 40°F when properly connected and operated. Temperature performance may vary based on ambient conditions, loading, door usage, electrical supply, and operating conditions. Optional Honda 3000 generators and interior shelving are available; pricing and availability are confirmed with each quote.",
  feature: "\xA0Temperature performance may vary based on ambient conditions, loading, door usage, electrical supply, and operating conditions.",
  image: "https://fridgefleet.com/wp-content/uploads/2026/09/ChatGPT-Image-Sep-12-2026-06_47_20-PM.png?v=autdsqyadjs",
  srcset: "https://fridgefleet.com/wp-content/uploads/2026/09/ChatGPT-Image-Sep-12-2026-06_47_20-PM.png?v=qxf5qfplbmr-wvc-srcset",
  sizes: "https://fridgefleet.com/wp-content/uploads/2026/09/ChatGPT-Image-Sep-12-2026-06_47_20-PM.png?v=af2rwr0g8xq-wvc-sizes",
  alt: "Fridge Fleet freezer trailer parked at a warehouse loading dock."
}, {
  id: "dual-temp-mobile",
  title: "7 × 16 Refrigerated & Freezer Trailer",
  tag: "Verified specifications",
  description: "All trailers operate using one standard 115-volt, 15-amp electrical circuit and maintain temperatures between −10°F and 40°F when properly connected and operated. Temperature performance may vary based on ambient conditions, loading, door usage, electrical supply, and operating conditions. Optional Honda 3000 generators and interior shelving are available; pricing and availability are confirmed with each quote.",
  feature: "\xA0Temperature performance may vary based on ambient conditions, loading, door usage, electrical supply, and operating conditions.",
  image: "https://fridgefleet.com/wp-content/uploads/2026/09/7x16-FF-trailer.png?v=n3acl4xg4h",
  srcset: "https://fridgefleet.com/wp-content/uploads/2026/09/7x16-FF-trailer.png?v=itm8x2wbh2-wvc-srcset",
  sizes: "https://fridgefleet.com/wp-content/uploads/2026/09/7x16-FF-trailer.png?v=hdbiewthark-wvc-sizes",
  alt: "Fridge Fleet mobile refrigeration trailer parked backstage at an outdoor event."
}, {
  id: "fifty-three-foot-reefer",
  title: "53-Foot Reefer Trailer",
  tag: "Specifications pending confirmation",
  description: "Large-capacity temporary refrigerated storage for commercial needs. Availability, specifications, delivery requirements, intended use, and temperature requirements are confirmed individually.",
  image: "https://fridgefleet.com/wp-content/uploads/2026/09/FF-Reefer-trailer.png",
  srcset: "https://fridgefleet.com/wp-content/uploads/2026/09/FF-Reefer-trailer.png-wvc-srcset",
  sizes: "https://fridgefleet.com/wp-content/uploads/2026/09/FF-Reefer-trailer.png-wvc-sizes",
  alt: "Fridge Fleet 53-foot reefer trailer for temporary refrigerated storage",
  linkTo: "/FootRefrigeratedTrailerRentalsPage",
  linkLabel: "View Trailer Details"
}];
export default function Fleet() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="trailer-fleet-overview" data-nav="light" className="bg-background py-14 md:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 border border-accent/40 text-accent-foreground text-xs font-bold tracking-wider uppercase mb-4">
            <ThermometerSnowflake className="size-3.5" />
            <span>EQUIPMENT SELECTION</span>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground font-sans mb-4">
            Mobile Cold Storage Trailer Options
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Choose from 6 × 8, 7 × 12, and 7 × 16 refrigerated and freezer trailers, plus a 53-foot reefer trailer for larger temporary cold-storage needs.
          </p>
        </div>

        {/* 2-Column Trailer Lineup Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {trailers.map((trailer, i) => <motion.div key={trailer.id} data-index={i} variants={cardVariants} className="h-full">
              <Card className="h-full flex flex-col justify-between overflow-hidden rounded-lg border border-border bg-card shadow-sm hover:shadow-md hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                <div>
                  {/* Aspect Ratio 16:9 Image Container */}
                  <div className="relative aspect-video w-full overflow-hidden bg-muted border-b border-border/60">
                    <img className="w-full h-full object-cover" src={trailer.image} data-wvc-srcset={trailer.srcset} data-wvc-sizes={trailer.sizes} alt={trailer.alt} loading="lazy" />
                  </div>

                  <CardContent className="p-6">
                    <div className="mb-3">
                      <Badge variant="outline" className="bg-muted text-muted-foreground border-border text-[11px] font-normal py-0.5 px-2">
                        {trailer.tag}
                      </Badge>
                    </div>

                    <h3 className="text-xl font-semibold tracking-tight text-card-foreground font-sans mb-2.5">
                      {trailer.title}
                    </h3>

                    <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                      {trailer.description}
                    </p>

                    {trailer.feature && <div className="flex items-center gap-2 text-xs font-medium text-foreground/80 pt-3 border-t border-border/60">
                        <ShieldCheck className="size-4 text-accent-foreground shrink-0" />
                        <span>{trailer.feature}</span>
                      </div>}
                  </CardContent>
                </div>

                <div className="p-6 pt-0 mt-2 border-t border-border/40">
                  <Link to={trailer.linkTo || "/RefrigeratedTrailersPage"} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors pt-4 group">
                    <span>{trailer.linkLabel || "View Specifications"}</span>
                    <ArrowRight className="size-4 transition-transform duration-150 group-hover:translate-x-1" />
                  </Link>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Footer Support Callout */}
        <div className="mt-12 text-center">
          <p className="text-xs sm:text-sm text-muted-foreground leading-normal">
            Full trailer dimensions, construction details, and power requirements are available on the Refrigerated Trailers page.{" "}
            <Link to="/ContactRequestAQuotePage" className="text-foreground font-medium underline underline-offset-4 hover:text-primary transition-colors">
              Request a custom quote
            </Link>
          </p>
        </div>
      </div>
    </section>;
}